import { createSelector } from "@reduxjs/toolkit";
import { getMedallionCounts } from "libs/character";
import { buildFighterData } from "libs/fight";
import { getCharacterSkills } from "libs/skill";
import {
  calculateCurrentBaseStats,
  calculateTotalBaseStats,
  getDerivedStatSets,
  getFilteredBattleRatings,
} from "libs/stats";
import { CHARACTER_FIGHTER_ACTIONS } from "redux/actions";
import {
  AuthOverlayScreens,
  LogMessage,
  MainMenuScreens,
  NewCharacterScreens,
  PlayerDetailsModel,
  PlaylistModel,
  RootState,
} from "types";

export const getGameModeInfo = createSelector(
  (state: RootState) => state.game.isInitialDataLoaded,
  (state: RootState) => state.game.isGameModeActive,
  (state: RootState) => state.game.mainMenuScreen,
  (state: RootState) => state.game.newCharacterScreen,
  (
    isInitialDataLoaded,
    isGameModeActive,
    mainMenuScreen,
    newCharacterScreen
  ): {
    isGameModeActive: boolean;
    isInitialDataLoaded: boolean;
    mainMenuScreen: MainMenuScreens;
    newCharacterScreen: NewCharacterScreens;
  } => {
    return {
      isGameModeActive,
      isInitialDataLoaded,
      mainMenuScreen,
      newCharacterScreen,
    };
  }
);

export const getAuthInfo = createSelector(
  (state: RootState) => state.game.isNewUser,
  (state: RootState) => state.game.isUserAccountLinked,
  (state: RootState) => state.game.isAuthOverlayOpen,
  (state: RootState) => state.game.authOverlayScreen,
  (
    isNewUser,
    isUserAccountLinked,
    isAuthOverlayOpen,
    authOverlayScreen
  ): {
    isNewUser: boolean;
    isUserAccountLinked: boolean;
    isAuthOverlayOpen: boolean;
    authOverlayScreen: AuthOverlayScreens;
  } => {
    return {
      isNewUser,
      isUserAccountLinked,
      isAuthOverlayOpen,
      authOverlayScreen,
    };
  }
);

export const getWorldDisplayInfo = createSelector(
  (state: RootState) => state.game.isHideWorld,
  (isHideWorld): { isHideWorld: boolean } => {
    return { isHideWorld };
  }
);

export const getToastMessages = (state: RootState): string[] =>
  state.game.messages;

export const getLogMessagesInfo = createSelector(
  (state: RootState) => state.game.logMessages,
  (state: RootState) => state.game.mutedUsers,
  (
    logMessages,
    mutedUsers
  ): {
    logMessages: LogMessage[];
    mutedUsers: string[];
  } => {
    return {
      logMessages,
      mutedUsers,
    };
  }
);

export const getMusicInfo = createSelector(
  (state: RootState) => state.game.isMusicPlaying,
  (state: RootState) => state.game.currentTrack,
  (state: RootState) => state.game.currentPlaylist,
  (state: RootState) => state.game.trackChanges,
  (
    isMusicPlaying,
    currentTrack,
    currentPlaylist,
    trackChanges
  ): {
    isMusicPlaying: boolean;
    currentTrack: string;
    currentPlaylist: PlaylistModel;
    trackChanges: number;
  } => {
    return { isMusicPlaying, currentTrack, currentPlaylist, trackChanges };
  }
);

// ONLINE PLAYERS

export const getSectorOnlinePlayers = (state: RootState) =>
  state.game.sectorOnlinePlayers;

export const getPlayerDetails = createSelector(
  (state: RootState) => state.game.playerDetails,
  (player): PlayerDetailsModel | null => {
    if (!player) {
      return null;
    }

    const {
      ship,
      pilot,
      location,
      ui,
      installedUpgrades,
      trainedSkills,
      enhancedBaseStats,
      weakenedBaseStats,
      battleRatings,
      damage,
      skillsRecharge,
      skillsValues,
    } = player.data;

    const filteredBattleRatings = getFilteredBattleRatings(battleRatings);

    const totalBaseStats = calculateTotalBaseStats(ship, enhancedBaseStats);
    const currentBaseStats = calculateCurrentBaseStats(
      totalBaseStats,
      weakenedBaseStats
    );
    const derivedStats = getDerivedStatSets(
      currentBaseStats,
      totalBaseStats,
      installedUpgrades
    );

    const skills = getCharacterSkills(
      pilot,
      trainedSkills,
      derivedStats.current.complete.fasterRecharge
    );
    const medallions = getMedallionCounts(
      filteredBattleRatings,
      ship,
      enhancedBaseStats,
      skills
    );
    const fighterData = buildFighterData(
      player.userName || "Player",
      derivedStats.current.complete,
      currentBaseStats,
      totalBaseStats,
      skills,
      skillsRecharge,
      skillsValues,
      damage,
      0,
      CHARACTER_FIGHTER_ACTIONS
    );

    return {
      userId: player.userId,
      userName: player.userName || "Player",
      ship,
      pilot,
      medallions,
      installedUpgrades,
      totalBaseStats,
      derivedStats,
      skills,
      battleRatings: filteredBattleRatings,
      location,
      ui,
      fighterData,
    };
  }
);
